import {BaseCard} from "../../Components/Cards/BaseCard";
import {useEffect, useState} from "react";
import {useBoundStore} from "../../Store/store";
import * as React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {faUser} from "@fortawesome/free-solid-svg-icons";
import {IconInputCard} from "../../Components/Cards/IconInputCard";
import {ShiftInfoCard} from "../../Components/Cards/ShiftInfoCard";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";

export const ShiftApplications = () => {
    const {t} = useTranslation();
    const {
        shiftId
    } = useParams();
    const navigate = useNavigate();

    const getShiftDetail = useBoundStore((state) => state.getShiftDetail);
    const shiftDetail = useBoundStore((state) => state.shiftDetail);
    const updateShiftPublished = useBoundStore((state) => state.updateShiftPublished);
    const setApplicationAsReserved = useBoundStore((state) => state.setApplicationAsReserved);
    const updateShift = useBoundStore((state) => state.updateShift);

    const [reservingId, setReservingId] = useState(null);

    useEffect(() => {
        getShiftDetail(shiftId);
    }, [getShiftDetail, shiftId]);

    const isShiftConfirmed = shiftDetail?.freelancer?.id != null;

    const handlePublishedChange = (newValue) => {
        if (isShiftConfirmed) return;
        updateShiftPublished(shiftId, newValue);
    };

    const handleReserveToggle = async (applicationId, currentReservedStatus) => {
        if (isShiftConfirmed || currentReservedStatus) return;

        setReservingId(applicationId);
        try {
            const result = await setApplicationAsReserved(applicationId, shiftId);
            if (result.success) {
                await getShiftDetail(shiftId);
            }
        } finally {
            setReservingId(null);
        }
    };

    const handleBreakDurationChange = async (newBreakDuration) => {
        if (isShiftConfirmed) return;

        try {
            const updateData = {
                start_time: dayjs(shiftDetail.start_time).format('YYYY-MM-DD HH:mm:ss'),
                end_time: dayjs(shiftDetail.end_time).format('YYYY-MM-DD HH:mm:ss'),
                branch_id: shiftDetail.branch?.id,
                break_duration: newBreakDuration,
                is_published: shiftDetail.is_published,
                remarks: shiftDetail.remarks,
                shift_applications_possibility: shiftDetail.shift_applications_possibility ?? 'both',
            };

            await updateShift(shiftId, updateData);
            toast.success(t('shifts.toast_break_updated'));
            await getShiftDetail(shiftId);
        } catch (error) {
            console.error('Error updating break duration:', error);
        }
    };

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between'}>
                    <h1 className={'text-2xl font-semibold'}>{t('shifts.applications_title')}</h1>
                </div>
            </div>

            {isShiftConfirmed && (
                <div className="mt-4 mb-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">
                                {t('shifts.already_confirmed_title')}
                            </h3>
                            <p className="text-sm text-green-700 mt-1">
                                {t('shifts.already_confirmed_message')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className={'grid grid-cols-1 md:grid-cols-3 md:space-x-4 space-y-4 md:space-y-0'}>
                <BaseCard border={false} className={'md:col-span-2'}>
                    {isShiftConfirmed ? (
                        <div className={'p-4'}>
                            <h3 className="text-lg font-semibold mb-4">{t('shifts.confirmed_freelancer')}</h3>
                            <div className="grid grid-cols-1 gap-4">
                                <IconInputCard
                                    icon={faUser}
                                    isImage={true}
                                    onClick={() => {}}
                                    showArrow={false}
                                    className="border-2 border-green-500"
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex-1">
                                            <p className={'text-sm font-semibold'}>{shiftDetail.freelancer.first_name} {shiftDetail.freelancer.last_name}</p>
                                            <p className={'text-xs text-gray-600 mt-1'}>{t('shifts.email_prefix')} {shiftDetail.freelancer.email || '-'}</p>
                                            <p className={'text-xs text-gray-600'}>{t('shifts.phone_prefix')} {shiftDetail.freelancer.phone_number || '-'}</p>
                                        </div>
                                        <div className="flex-shrink-0 ml-4">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                {t('general.status.confirmed')}
                                            </span>
                                        </div>
                                    </div>
                                </IconInputCard>
                            </div>
                        </div>
                    ) : (
                        <div className={'grid grid-cols-1 xl:grid-cols-2 p-4 gap-4'}>
                            {
                                shiftDetail?.applications?.map((application, index) => {
                                    const isReserved = application?.reserved_at !== null && application?.reserved_at !== undefined;
                                    const isReserving = reservingId === application.id;

                                    return (
                                        <div key={index} className="relative">
                                            <IconInputCard
                                                icon={faUser}
                                                isImage={true}
                                                onClick={() => {
                                                    navigate(`/customer/shifts/${shiftId}/applications/${application.id}`)
                                                }}
                                                showArrow={true}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div className="flex-1">
                                                        <p className={'text-xs'}>{application.freelancer?.first_name} {application.freelancer?.last_name}</p>
                                                        <p className={'text-xs text-gray-600'}>{t('shifts.remark_prefix')} {application?.remark ? application.remark : '-'}</p>
                                                    </div>

                                                    <div className="flex items-center ml-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                        <label className="flex items-center cursor-pointer">
                                                            <span className="text-xs mr-2 text-gray-700 whitespace-nowrap">{t('shifts.reserved')}</span>
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isReserved}
                                                                    onChange={() => handleReserveToggle(application.id, isReserved)}
                                                                    disabled={isReserving || isReserved}
                                                                    className="sr-only"
                                                                />
                                                                <div className={`block w-10 h-6 rounded-full transition ${
                                                                    isReserved ? 'bg-green-500' : 'bg-gray-300'
                                                                } ${isReserving ? 'opacity-50' : ''}`}>
                                                                </div>
                                                                <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${
                                                                    isReserved ? 'transform translate-x-4' : ''
                                                                }`}>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    </div>
                                                </div>
                                            </IconInputCard>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    )}

                </BaseCard>

                <BaseCard border={false}>
                    <ShiftInfoCard
                        shift={shiftDetail}
                        onPublishedChange={handlePublishedChange}
                        onBreakDurationChange={handleBreakDurationChange}
                        readOnly={isShiftConfirmed}
                    />
                </BaseCard>
            </div>


        </div>
    )
}